import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FormMode } from 'src/app/controls/common/form-mode';
import { HttpError } from 'src/app/core/http-error';
import { IOrganization } from 'src/app/models/interfaces/organization.interface';
import { IParamsValues } from 'src/app/models/interfaces/params-values.interface';
import { ParamsValues } from 'src/app/models/params-values';
import { NotificationService } from 'src/app/services/notification-service';
import { ParamsValuesService } from 'src/app/services/params-values.service';
import { ParametersValuesFormContext } from '../../parameters/parameters-values-form/parameters-values-form-context';
import { ParametersValuesFormComponent } from '../../parameters/parameters-values-form/parameters-values-form.component';
import { OrganizationDetailContext } from '../organization-detail-context';
import { OrganizationFormComponent } from '../organization-form/organization-form.component';

@Component({
    selector: 'app-organization-detail',
    templateUrl: './organization-detail.component.html',
    styleUrls: ['./organization-detail.component.scss'],
    imports: [MatTabsModule, OrganizationFormComponent, ParametersValuesFormComponent],
})
export class OrganizationDetailComponent implements OnInit {
    /**
     * Mode d'affichage du formulaire (consultation / edition / création)
     */
    public FormMode = FormMode;

    /**
     * Reference au composant groupe d'onglets.
     */
    @ViewChild(MatTabGroup)
    tabGroup: MatTabGroup;

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    @Output()
    public organizationUpdated: EventEmitter<IOrganization> = new EventEmitter();

    /**
     * Indique si le composant est en mode debug (affiche des informations pour le developpeur)
     */
    public debugMode = false;

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    @Output()
    public paramsValuesUpdated: EventEmitter<IParamsValues> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est annuler(clic sur le bouton annuler)
     */
    @Output()
    public canceled: EventEmitter<boolean> = new EventEmitter();

    /**
     * Contexte du formulaire.
     */
    public _context: OrganizationDetailContext;

    /**
     * Utilisé pour propager des changements de contexte.
     */
    public _context$: BehaviorSubject<OrganizationDetailContext> = new BehaviorSubject(null);

    /**
     * Appellé par le composant parent pour specifier un nouveau contexte.
     */
    @Input()
    set context(value: OrganizationDetailContext) {
        // set the latest value for _data BehaviorSubject
        this._context$.next(value);
    }

    /**
     * Contexte des paramètres.
     */
    public _paramsContext: ParametersValuesFormContext;

    constructor(
        private paramsValuesService: ParamsValuesService,
        private spinner: NgxSpinnerService,
        protected notificationService: NotificationService
    ) {}

    /**
     * Initialise le composant.
     */
    ngOnInit(): void {
        /**
         * S'abonne aux changements de contexte
         */
        this._context$.subscribe((x) => {
            this._context = x;

            if (this._context) {
                this.spinner.show('main-spinner');
                this.loadOrganizationParamsValues(this._context.editedItemId).subscribe(
                    (paramsValues) => {
                        this._paramsContext = new ParametersValuesFormContext();
                        this._paramsContext.formMode = this._context.formMode;
                        this._paramsContext.paramsValues = paramsValues;
                        this.spinner.hide('main-spinner');
                    },
                    (error: HttpError) => {
                        this.notificationService.displayError('Erreur', "Impossible de récupérer le paramétrage de l'organisation." + error.message);
                        this.spinner.hide('main-spinner');
                    }
                );
            }
        });
    }

    /**
     * Charge les données du jeu de paramètres du groupe.
     * @param organizationId : Id de l'organisation à charger.
     */
    private loadOrganizationParamsValues(organizationId: string): Observable<ParamsValues> {
        return this.paramsValuesService.getForOrganization(organizationId);
    }

    /**
     * Appellé lorsque des changements sur le formulaire "infos" sont sauvegardés.
     * @param organization: Infos de l'organisation modifié.
     */
    public onOrganizationUpdated(organization: IOrganization) {
        this.organizationUpdated.emit(organization);
    }

    public onParamsValuesUpdated(paramsvalues: IParamsValues) {
        this.paramsValuesUpdated.emit(paramsvalues);
    }

    public onParamsExported() {
        this.paramsValuesService
            .exportForOrganization(this._context.editedItemId)
            .pipe(
                catchError((err) => {
                    console.error('Export Error');
                    return of(null);
                })
            )
            .subscribe((blob) => {
                const a = document.createElement('a');
                const objectUrl = URL.createObjectURL(blob);
                a.href = objectUrl;
                a.download = `Paramètres de l\`organisation ${this._context.editedItemId}.xlsx`;
                a.click();
                URL.revokeObjectURL(objectUrl);
            });
    }

    /**
     * Appellé lorsque des changements sur le formulaire "infos" sont annulés.
     * @param user: Infos de l'article modifié.
     */
    public onCanceled(value: boolean) {
        this.canceled.emit(value);
    }
}
